// Import React so we can create components
import React from "react";

// Import the component styling
import "./RoleVisual.css";

// Exported component in PascalCase (project convention)
export default function RoleVisual({ roleKey, productFamily, title }) {

  // Convert the raw role key into a normalized surface like "front"
  const surface = getSurfaceFromRoleKey(roleKey);

  // Decide which illustration set to use (shirt vs generic cube)
  const VisualComponent = getVisualComponentByFamily(productFamily);

  // Return a wrapper with an accessible label and the SVG inside
  return (
    // Wrapper for consistent sizing and alignment
    <div className="role-visual" aria-label={title || surface || "Role"}>
      
      {/* Render the selected illustration */}
      <VisualComponent surface={surface} />

    </div>
  );
}

// Internal helper (camelCase) to pick a visual set based on product family
function getVisualComponentByFamily(productFamily) {

  // Normalize the family to lowercase for safety
  const safeFamily = String(productFamily || "").toLowerCase();

  // If this is a shirt-like product, use the shirt silhouette
  if (safeFamily.includes("shirt") || safeFamily.includes("tshirt") || safeFamily.includes("tee")) {

    // Return the shirt visual component
    return ShirtVisual;

  }

  // For boxes / money boxes you *could* add a specific set later
  // if (safeFamily.includes("box") || safeFamily.includes("moneybox")) {
  //   return BoxVisual;
  // }

  // Fallback to a generic cube that works for many products
  return CubeVisual;
}

// Internal helper (camelCase) to extract the surface from the role key
function getSurfaceFromRoleKey(roleKey) {

  // Convert to a safe string
  const key = String(roleKey || "").toLowerCase();

  // If the key includes "front" treat it as front
  if (key.includes("front")) return "front";

  // If the key includes "back" treat it as back
  if (key.includes("back")) return "back";

  // If the key includes "left" treat it as left
  if (key.includes("left")) return "left";

  // If the key includes "right" treat it as right
  if (key.includes("right")) return "right";

  // If the key includes "top" treat it as top
  if (key.includes("top")) return "top";

  // If the key includes "bottom") treat it as bottom
  if (key.includes("bottom")) return "bottom";

  // If we can’t detect it, return unknown
  return "unknown";
}

// Generic cube visual (3D cube) with "x-ray" mode for hidden faces
function CubeVisual({ surface }) {

  // Decide if we should show the cube in x-ray mode for hidden faces
  const isXrayMode = surface === "back" || surface === "left" || surface === "bottom";

  // Define the front face points (a square)
  const A = { x: 30, y: 50 };

  // Define the front face points (a square)
  const B = { x: 70, y: 50 };

  // Define the front face points (a square)
  const C = { x: 70, y: 90 };

  // Define the front face points (a square)
  const D = { x: 30, y: 90 };

  // Define the back face offset (shift right and up to create 3D)
  const dx = 22;

  // Define the back face offset (shift right and up to create 3D)
  const dy = -18;

  // Define the back face points by offsetting the front face
  const A2 = { x: A.x + dx, y: A.y + dy };

  // Define the back face points by offsetting the front face
  const B2 = { x: B.x + dx, y: B.y + dy };

  // Define the back face points by offsetting the front face
  const C2 = { x: C.x + dx, y: C.y + dy };

  // Define the back face points by offsetting the front face
  const D2 = { x: D.x + dx, y: D.y + dy };

  // Helper to convert point objects into an SVG "points" string
  const toPoints = (pts) => {

    // Join all points as "x,y" pairs for SVG polygons
    return pts.map((p) => `${p.x},${p.y}`).join(" ");

  };

  // Helper to build a className for each face
  const getFaceClass = (faceName) => {

    // Base class for cube faces
    const base = "role-visual__cube-face";

    // Face-specific class so we can tweak if needed later
    const face = `${base}--${faceName}`;

    // Active modifier class if this is the selected face
    const active = faceName === surface ? `${base}--active` : "";

    // X-ray modifier if we’re in x-ray mode
    const xray = isXrayMode ? "role-visual__cube-face--xray" : "";

    // Return the final class string
    return [base, face, active, xray].filter(Boolean).join(" ");

  };

  // Return the SVG
  return (
    // SVG with predictable scaling
    <svg className="role-visual__svg" viewBox="0 0 120 120" role="img">

      {/* Draw BACK first so it sits behind (still visible in x-ray mode) */}
      <polygon
        className={getFaceClass("back")}
        points={toPoints([A2, B2, C2, D2])}
      />

      {/* Draw LEFT (normally hidden with this perspective, visible in x-ray mode) */}
      <polygon
        className={getFaceClass("left")}
        points={toPoints([A, D, D2, A2])}
      />

      {/* Draw BOTTOM (normally hidden, visible in x-ray mode) */}
      <polygon
        className={getFaceClass("bottom")}
        points={toPoints([D, C, C2, D2])}
      />

      {/* Draw TOP (normally visible) */}
      <polygon
        className={getFaceClass("top")}
        points={toPoints([A2, B2, B, A])}
      />

      {/* Draw RIGHT (normally visible) */}
      <polygon
        className={getFaceClass("right")}
        points={toPoints([B, B2, C2, C])}
      />

      {/* Draw FRONT last so it’s most readable */}
      <polygon
        className={getFaceClass("front")}
        points={toPoints([A, B, C, D])}
      />

    </svg>
  );

}



// Shirt visual (only one highlighted region, and a different silhouette for back vs front)
function ShirtVisual({ surface }) {

  // Decide if we should show the back silhouette (no front neckline dip)
  const isBackView = surface === "back";

  // Front silhouette path (includes neckline dip)
  const frontPath =
    "M40 20 " +
    "L52 15 " +
    "L68 15 " +
    "L80 20 " +
    "L95 35 " +
    "L85 45 " +
    "L80 40 " +
    "L80 100 " +
    "L40 100 " +
    "L40 40 " +
    "L35 45 " +
    "L25 35 " +
    "Z " +
    "M52 15 " +
    "Q60 26 68 15";

  // Back silhouette path (no neckline dip, flatter collar line)
  const backPath =
    "M40 20 " +
    "L52 15 " +
    "L68 15 " +
    "L80 20 " +
    "L95 35 " +
    "L85 45 " +
    "L80 40 " +
    "L80 100 " +
    "L40 100 " +
    "L40 40 " +
    "L35 45 " +
    "L25 35 " +
    "Z";

  // Pick the correct base path based on the view
  const basePath = isBackView ? backPath : frontPath;

  // Helper to render the ONE highlight shape for the current surface
  const renderHighlight = () => {

    // If the surface is front, highlight the main chest/torso panel
    if (surface === "front") {

      // Return a simple torso rectangle highlight (no stroke)
      return (
        <rect
          className="role-visual__shirt-highlight"
          x="45"
          y="42"
          width="30"
          height="52"
          rx="6"
        />
      );

    }

    // If the surface is back, highlight the back panel (bigger and higher)
    if (surface === "back") {

      // Return a back panel highlight (no stroke)
      return (
        <rect
          className="role-visual__shirt-highlight"
          x="44"
          y="30"
          width="32"
          height="62"
          rx="6"
        />
      );

    }

    // If the surface is left, highlight the left sleeve
    // If the surface is left, highlight the full left sleeve (shoulder to cuff)
    // If the surface is left, highlight the full left sleeve (shoulder to cuff)
    if (surface === "left") {

      // Return a full sleeve highlight (no stroke)
      return (

        // Draw a larger polygon that covers the whole sleeve
        <polygon

          // Use the shirt highlight class
          className="role-visual__shirt-highlight"

          // Inner point nudged outward to reduce torso overlap
          points="25,35 35,45 50,39 50,24 40,20 29,28"

        />

      );

    }

    // If the surface is right, highlight the full right sleeve (shoulder to cuff)
    if (surface === "right") {

      // Return a full sleeve highlight (no stroke)
      return (

        // Draw a larger polygon that covers the whole sleeve
        <polygon

          // Use the shirt highlight class
          className="role-visual__shirt-highlight"

          // Inner point nudged outward to reduce torso overlap
          points="95,35 85,45 70,39 70,24 80,20 91,28"

        />

      );

    }

    // If the surface is top, highlight a small strip at the collar/shoulder area
    if (surface === "top") {

      // Return a top strip highlight (no stroke)
      return (
        <rect
          className="role-visual__shirt-highlight"
          x="44"
          y="18"
          width="32"
          height="8"
          rx="4"
        />
      );

    }

    // If the surface is bottom, highlight a small hem strip
    if (surface === "bottom") {

      // Return a bottom strip highlight (no stroke)
      return (
        <rect
          className="role-visual__shirt-highlight"
          x="44"
          y="96"
          width="32"
          height="8"
          rx="4"
        />
      );

    }

    // If the surface is unknown, render no highlight
    return null;

  };

  // Return the SVG
  return (
    <svg className="role-visual__svg" viewBox="0 0 120 120" role="img">

      {/* Draw the shirt silhouette first */}
      <path
        className="role-visual__shirt-base"
        d={basePath}
      />

      {/* Draw ONLY the active highlight shape */}
      {renderHighlight()}

      {/* Optional label */}
      <text className="role-visual__label" x="60" y="118" textAnchor="middle">
        {surface === "unknown" ? "role" : surface}
      </text>

    </svg>
  );

}

